import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  FileDownload as FileDownloadIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'
import { formatCurrency, PaymentBatch } from './mockData'
import {
  getBatches,
  deleteBatch,
  getBatchCreatedByName,
  exportBatch,
  exportBatchesCSV,
  ApiBatch,
} from '@/services/batchService'

const statusChipSx = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return { bgcolor: '#dcfce7', color: '#166534' }
    case 'processing':
      return { bgcolor: '#e0f2fe', color: '#0369a1' }
    case 'pending':
    case 'ready for payment':
      return { bgcolor: '#fef3c7', color: '#92400e' }
    default:
      return { bgcolor: '#f1f5f9', color: '#475569' }
  }
}

const formatMonth = (batchMonth: string): string => {
  try {
    return format(parse(batchMonth, 'yyyy-MM', new Date()), 'MMMM yyyy')
  } catch {
    return batchMonth
  }
}

const safeDate = (value?: string): string => {
  if (!value) return '—'
  try {
    return format(new Date(value), 'MM/dd/yyyy')
  } catch {
    return value
  }
}

// Map an API batch into the PaymentBatch shape used by the detail page.
const toPaymentBatch = (b: ApiBatch): PaymentBatch => ({
  _id: b._id,
  name: b.batchName,
  month: formatMonth(b.batchMonth),
  description: b.description,
  createdBy: getBatchCreatedByName(b.createdBy),
  createdDate: b.createdAt,
  paymentDate: b.paymentDate,
  status: b.status as PaymentBatch['status'],
  totalProviders: b.totalProvider,
  totalInvoices: b.totalInvoice,
  totalSessions: 0,
  totalAmount: b.totalAmount,
  totalPayout: b.providerPayout ?? b.totalAmount,
  paymentMethod: '',
  providers: [],
  batchType: b.batchType,
  timeline: [{ label: 'Created', timestamp: b.createdAt }],
})

const SummaryCard: React.FC<{
  label: string
  value: string
  icon: React.ReactNode
}> = ({ label, value, icon }) => (
  <Card sx={{ p: 3, borderRadius: '1rem', boxShadow: 1 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <Box>
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {label}
        </Typography>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: '#7A6FA1', mt: 1.5 }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#7A6FA1',
        }}
      >
        {icon}
      </Box>
    </Box>
  </Card>
)

const AgencyPaymentBatchesPage: React.FC = () => {
  const navigate = useNavigate()
  const [batches, setBatches] = useState<ApiBatch[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [monthFilter, setMonthFilter] = useState('All Months')
  const [statusFilter, setStatusFilter] = useState('All Statuses')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)

  const [deleteTarget, setDeleteTarget] = useState<ApiBatch | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const [monthOptionMap, setMonthOptionMap] = useState<Record<string, string>>(
    {}
  )
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalProviderPayout: 0,
    totalProviderPayoutPaid: 0,
  })

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getBatches({
        page: 1,
        limit: 1000,
        search: search.trim() || undefined,
        batchMonth: monthFilter !== 'All Months' ? monthFilter : undefined,
        status: statusFilter !== 'All Statuses' ? statusFilter : undefined,
      })
      setBatches(res.data || [])
      setSummary({
        totalAmount: res.totalAmount || 0,
        totalProviderPayout: res.totalProviderPayout || 0,
        totalProviderPayoutPaid: res.totalProviderPayoutPaid || 0,
      })
    } catch (err) {
      console.error('Error fetching batches:', err)
      setBatches([])
    } finally {
      setLoading(false)
    }
  }, [search, monthFilter, statusFilter])

  // Debounced API-driven fetch whenever any filter changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBatches()
    }, 400)
    return () => clearTimeout(timer)
  }, [fetchBatches])

  // Accumulate month options so the dropdown stays stable while filtering.
  useEffect(() => {
    setMonthOptionMap(prev => {
      const next = { ...prev }
      batches.forEach(b => {
        if (b.batchMonth) next[b.batchMonth] = formatMonth(b.batchMonth)
      })
      return next
    })
  }, [batches])

  const handleExportCsv = async () => {
    setExportingCsv(true)
    try {
      await exportBatchesCSV({
        search: search.trim() || undefined,
        batchMonth: monthFilter !== 'All Months' ? monthFilter : undefined,
        status: statusFilter !== 'All Statuses' ? statusFilter : undefined,
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to export batches.',
        severity: 'error',
      })
    } finally {
      setExportingCsv(false)
    }
  }

  const handleExport = async (batch: ApiBatch) => {
    setExportingId(batch._id)
    try {
      await exportBatch(batch._id, `${batch.batchName || 'batch'}.csv`)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to export batch.',
        severity: 'error',
      })
    } finally {
      setExportingId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteBatch(deleteTarget._id)
      setSnackbar({
        open: true,
        message: 'Payout batch deleted successfully.',
        severity: 'success',
      })
      setDeleteTarget(null)
      await fetchBatches()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || 'Failed to delete payout batch.',
        severity: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  const monthOptions = useMemo(() => {
    const months = Object.entries(monthOptionMap)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([value, label]) => ({ value, label }))
    return [{ value: 'All Months', label: 'All Months' }, ...months]
  }, [monthOptionMap])

  const statusOptions = ['All Statuses', 'Pending', 'Paid', 'Cancelled']

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => {
    setPage(0)
  }, [search, monthFilter, statusFilter])

  const paginatedBatches = useMemo(
    () =>
      batches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [batches, page, rowsPerPage]
  )

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleReset = () => {
    setSearch('')
    setMonthFilter('All Months')
    setStatusFilter('All Statuses')
  }

  const openDetail = (batch: ApiBatch) => {
    navigate(`/agency-payment-batches/${batch._id}`, {
      state: { batch: toPaymentBatch(batch) },
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: '#7A6FA1', fontWeight: 600 }}
            gutterBottom
          >
            Payout Batches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Group provider invoices into batches and process payouts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            exportingCsv ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleExportCsv}
          disabled={exportingCsv}
          sx={{
            textTransform: 'none',
            borderRadius: '0.5rem',
            fontWeight: 500,
            bgcolor: '#7A6FA1',
            '&:hover': { bgcolor: '#655a8a' },
          }}
        >
          {exportingCsv ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Box>

      {/* Summary cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2.5,
          mb: 3,
        }}
      >
        <SummaryCard
          label="Total Amount"
          value={formatCurrency(summary.totalAmount)}
          icon={<ReceiptIcon />}
        />
        <SummaryCard
          label="Total Provider Payout"
          value={formatCurrency(summary.totalProviderPayout)}
          icon={<PendingIcon />}
        />
        <SummaryCard
          label="Total Provider Payout Paid"
          value={formatCurrency(summary.totalProviderPayoutPaid)}
          icon={<CheckCircleIcon />}
        />
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2.5, borderRadius: '1rem', boxShadow: 1, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ flex: '1 1 240px', minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Batch Name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.625rem' } }}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <Typography
              variant="body2"
              sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.8125rem' }}
            >
              Batch Month
            </Typography>
            <Select
              fullWidth
              size="small"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              sx={{ borderRadius: '0.625rem' }}
            >
              {monthOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <Typography
              variant="body2"
              sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.8125rem' }}
            >
              Status
            </Typography>
            <Select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              sx={{ borderRadius: '0.625rem' }}
            >
              {statusOptions.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Button
            variant="outlined"
            startIcon={<SyncIcon sx={{ fontSize: '18px' }} />}
            onClick={handleReset}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: '0.625rem',
              borderColor: '#7A6FA1',
              color: '#7A6FA1',
              height: '40px',
              '&:hover': { borderColor: '#655a8a', bgcolor: 'action.hover' },
            }}
          >
            Reset
          </Button>
        </Box>
      </Card>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '1rem', boxShadow: 1 }}>
        <Table sx={{ minWidth: 1100, '& th, & td': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#7A6FA1' }}>
              {['Batch Name', 'Type', 'Month', 'Providers', 'Invoices / Claims', 'Total Batch Amount', 'Provider Payout', 'Created By', 'Created Date', 'Payment Date', 'Status', 'Action'].map(
                head => (
                  <TableCell
                    key={head}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      textAlign: head === 'Action' ? 'right' : 'left',
                    }}
                  >
                    {head}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No payout batches found.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedBatches.map(batch => (
                <TableRow key={batch._id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {batch.batchName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={batch.batchType === 'claim' ? 'Claim' : 'Invoice'}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        bgcolor:
                          batch.batchType === 'claim' ? '#ede9fe' : '#e0f2fe',
                        color:
                          batch.batchType === 'claim' ? '#6d28d9' : '#0369a1',
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatMonth(batch.batchMonth)}</TableCell>
                  <TableCell>{batch.totalProvider}</TableCell>
                  <TableCell>
                    {batch.batchType === 'claim'
                      ? batch.totalClaim
                      : batch.totalInvoice}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(batch.totalAmount)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(batch.providerPayout || 0)}
                  </TableCell>
                  <TableCell>{getBatchCreatedByName(batch.createdBy)}</TableCell>
                  <TableCell>{safeDate(batch.createdAt)}</TableCell>
                  <TableCell>{safeDate(batch.paymentDate)}</TableCell>
                  <TableCell>
                    <Chip
                      label={batch.status}
                      size="small"
                      sx={{ ...statusChipSx(batch.status), fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Tooltip placement="top" title="View details batch">
                        <IconButton
                          size="small"
                          onClick={() => openDetail(batch)}
                          sx={{ color: '#7A6FA1' }}
                        >
                          <PublishedWithChangesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip placement="top" title="Download Report">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleExport(batch)}
                            disabled={exportingId === batch._id}
                            sx={{ color: '#7A6FA1' }}
                          >
                            {exportingId === batch._id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <FileDownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip placement="top" title="Delete Batch">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(batch)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && batches.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <AdvancedPagination
            count={batches.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete payout batch</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.batchName}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            variant="outlined"
            disabled={deleting}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AgencyPaymentBatchesPage
